import { useLiveNotice } from '../Component/LiveFeedback';
import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';
import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import api from "../../../../Component/axios_Interceptor/api";
import {
  product_option_load,
} from "../../../../Component/Interface/Live";
import url from "../../../../Component/server";
import { fontStyles } from "../../../../Component/style/admin_font";
import {
  BackgroundArea,
  RowView,
} from "../../../../Component/style/common_style";
import OptionBox_list, { OptionBoxListHandle } from "./OptionBox_list";
import { StoreInfo } from "../../../../Component/Interface/Store";
import ConfirmModal from "../../../../Component/UI/ConfirmModal";
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';

type OptionData = {
  yn: boolean;
  seq: number;
  product_status: string;
  live_seq: number;
  store_info: StoreInfo;
  current_cnt?: number;
  stockOnly?: boolean;
  structureLocked?: boolean;
};

const OptionBox = forwardRef(
  ({ updateChnage, bulkStockControls = false }: { updateChnage: () => void; bulkStockControls?: boolean }, ref) => {
  const notifyLive = useLiveNotice();
    const [product_seq, setProduct_seq] = useState<number>(0);
    const [live_seq, setLive_seq] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const [product_status, setProduct_status] = useState("");
    const [previousSaleStatus, setPreviousSaleStatus] = useState("");
    const [changingStatus, setChangingStatus] = useState(false);
    const [stockBeforeOptions, setStockBeforeOptions] = useState(0);
    const [pendingOpen, setPendingOpen] = useState<OptionData | null>(null);
    const closing = useRef(false);
    const [stockOnly, setStockOnly] = useState(false);
    const [structureLocked, setStructureLocked] = useState(false);
    const isNotModi = stockOnly || structureLocked || product_status !== "대기";

    useImperativeHandle(ref, () => ({ setData }));

    const openEditor = (data: OptionData) => {
      setProduct_seq(data.seq);
      setProduct_status(data.product_status);
      setStockOnly(!!data.stockOnly);
      setStructureLocked(!!data.structureLocked);
      setStockBeforeOptions(data.current_cnt ?? 0);
      setLive_seq(data.live_seq);
      setIsOpen(true);
    };

    const setData = (data: OptionData) => {
      if (savingRef.current || closing.current || changingStatus) return;
      setPreviousSaleStatus("");
      if (data.stockOnly || data.product_status === "대기") openEditor(data);
      else setPendingOpen(data);
    };

    const changeStatus = async (seq: number, liveSeq: number, status: string) => {
      const response = await api.patch(url.Communication_url.product_update, {
        live_seq: liveSeq, product_list: [{ seq, product_status: status }],
      });
      if (response.data?.total !== 1 || response.data?.success !== 1 || response.data?.fail !== 0) {
        throw new Error("상품 상태를 변경하지 못했습니다.");
      }
      updateChnage();
    };

    const beginEditing = async () => {
      if (!pendingOpen || changingStatus) return;
      const data = pendingOpen;
      setChangingStatus(true);
      try {
        await changeStatus(data.seq, data.live_seq, "대기");
        setPreviousSaleStatus(data.product_status);
        setPendingOpen(null);
        openEditor({ ...data, product_status: "대기" });
      } catch (error: any) {
        notifyLive(error?.response?.data?.message || error?.message || "대기 상태로 전환하지 못했습니다.");
      } finally {
        setChangingStatus(false);
      }
    };

    const resetModal = () => {
      setIsOpen(false);
      setProduct_seq(0);
      setProduct_status("");
      setPreviousSaleStatus("");
    };

    const closeModal = async () => {
      if (changingStatus || closing.current || savingRef.current) return;
      closing.current = true;
      try {
        if (previousSaleStatus) await restoreSaleStatus();
        else resetModal();
      } finally { closing.current = false; }
    };
    useEffect(() => {
      if (!isOpen) return;
      const onEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape" && !event.isComposing) { event.preventDefault(); void closeModal(); }
      };
      document.addEventListener("keydown", onEscape);
      return () => document.removeEventListener("keydown", onEscape);
    });

    const restoreSaleStatus = async () => {
      if (changingStatus) return;
      setChangingStatus(true);
      try {
        const response = await api.get(url.Communication_url.product_option_info, { params: { product_seq } }).catch(error => {
          if (error?.response?.status === 404) return { data: { options: [] } };
          throw error;
        });
        if (!Array.isArray(response.data?.options)) throw new Error("옵션 목록을 확인하지 못했습니다.");
        if (response.data.options.length > 0 && stockBeforeOptions > 0 && !response.data.options.some((option: product_option_load) => Number(option.current_cnt) > 0)) {
          notifyLive("기존 재고를 옵션별로 입력한 뒤 판매 상태로 전환해주세요.");
          return;
        }
        await changeStatus(product_seq, live_seq, previousSaleStatus);
        resetModal();
      } catch (error: any) {
        notifyLive(error?.response?.data?.message || error?.message || "판매 상태로 복구하지 못했습니다.");
      } finally {
        setChangingStatus(false);
      }
    };

    const productOptionListRef = useRef<OptionBoxListHandle>(null);
    const saveOptions = async () => {
      if (savingRef.current || changingStatus || !productOptionListRef.current?.validate()) return;
      savingRef.current = true; setSaving(true);
      try {
        if (!(await productOptionListRef.current?.flush())) return;
        if (previousSaleStatus) await restoreSaleStatus();
      } catch (error: any) {
        notifyLive(error?.response?.data?.message || '옵션 설정 저장에 실패했습니다. 다시 시도해주세요.');
      } finally { savingRef.current = false; setSaving(false); }
    };

    return (<>
      <BackgroundArea data-admin-modal-overlay="" data-testid="option-backdrop" style={!isOpen ? { display: "none" } : {}} onClick={event => { if (event.target === event.currentTarget) void closeModal(); }}>
        <ModalBox data-admin-modal="" role={isOpen ? "dialog" : undefined} aria-modal={isOpen ? true : undefined} aria-label="옵션 설정">
          <TitleArea data-admin-modal-header="">
            <div>옵션 설정</div>

            <HeaderActions>
              <ModalCloseButton tone="admin" aria-label="옵션 설정 닫기" disabled={saving} onClick={() => void closeModal()} />
            </HeaderActions>
          </TitleArea>

          <ModalScroll data-admin-modal-body="" disabled={saving}>
          {previousSaleStatus && <StatusNotice>옵션 수정 중 · 대기 상태{changingStatus ? " · 상태 변경 중" : ""}</StatusNotice>}
          {stockOnly && <StatusNotice>남은 재고 수량을 입력한 뒤 저장을 눌러주세요.</StatusNotice>}
          {!stockOnly && stockBeforeOptions > 0 && <StatusNotice>기존 재고 {stockBeforeOptions}개를 옵션별 재고로 입력해주세요.</StatusNotice>}

            <OptionBox_list
              ref={productOptionListRef}
              syncOptionMode bulkStockControls={bulkStockControls} isNotModi={isNotModi} stockEditable={stockOnly} isOpen={isOpen}
              product_seq={product_seq} updateChnage={updateChnage}
            />
          </ModalScroll>
          <div data-admin-modal-footer="">
              <AdminActionButton $primary type="button" disabled={saving || changingStatus} onClick={() => void saveOptions()}>{saving ? '저장 중…' : '저장'}</AdminActionButton>
          </div>
        </ModalBox>
      </BackgroundArea>
      <ConfirmModal visible={!!pendingOpen} title="옵션 수정" subtitle="옵션은 대기 상태에서 변경할 수 있습니다. 대기 상태로 전환할까요?"
        onCancel={() => setPendingOpen(null)} onConfirm={() => void beginEditing()} confirmText="대기로 전환" />
      </>
    );
  }
);

export default OptionBox;

const StatusNotice = styled.p`
  margin: 0 0 var(--space-3);
  padding: 0.65rem 0.8rem;
  border-radius: 6px;
  background: var(--admin-theme-accent-soft, #f5f1ff);
  color: var(--admin-theme-link, #6541cf);
  font-size: 0.85rem;
`;

const ModalBox = styled.div`
  box-sizing: border-box;
  width: calc(100% - 24px);
  margin: auto;
  background-color: var(--admin-theme-surface, white);
  color: var(--admin-ink);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  max-width: 720px;
  max-height: calc(100dvh - 48px);
  display: flex; flex-direction: column; overflow: hidden;

`;
const TitleArea = styled(RowView)`
  flex-shrink: 0;
  margin-bottom: var(--space-4);
  div {
    ${fontStyles.medium}
    font-size: 20px;
  }
  img {
    cursor: pointer;
  }
`;
const HeaderActions = styled.div`display: flex; align-items: center; gap: var(--space-2);`;
const ModalScroll = styled.fieldset`border: 0; margin: 0; padding: 0 var(--space-2) 0 0; min-width: 0; min-height: 0; flex: 1; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable;`;
